/**
 * ImageLightbox — full-screen media viewer opened when the user clicks a
 * media thumbnail. Extracted byte-verbatim from KnowledgeGraphPage.jsx in
 * SPD v5.53 Phase E.
 *
 * Contract:
 *   - lightbox : { src?, thumb?, title, sourceUrl?, source? } | null (parent-owned)
 *   - onClose  : () => void  (dismisses the lightbox)
 *
 * State is parent-owned; this component only renders when lightbox is set.
 */
import React from 'react';
import { ExternalLink } from 'lucide-react';

export default function ImageLightbox({ lightbox, onClose }) {
  if (!lightbox) return null;
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="image-lightbox"
    >
      <div className="absolute inset-0 bg-black/90 backdrop-blur-sm" />
      <div className="relative z-10 max-w-[92vw] max-h-[92vh] flex flex-col items-center gap-3">
        <img
          src={lightbox.src || lightbox.thumb}
          alt={lightbox.title}
          referrerPolicy="no-referrer"
          className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
          onClick={(e) => e.stopPropagation()}
          onError={(e) => { if (lightbox.thumb && e.currentTarget.src !== lightbox.thumb) e.currentTarget.src = lightbox.thumb; }}
        />
        <div className="text-center text-white max-w-2xl px-4" onClick={(e) => e.stopPropagation()}>
          <p className="text-sm font-semibold line-clamp-2">{lightbox.title}</p>
          {lightbox.sourceUrl && (
            <a
              href={lightbox.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 mt-1 text-xs text-cyan-300 hover:text-cyan-200"
              data-testid="lightbox-source-link"
            >
              <ExternalLink className="h-3 w-3" />
              {lightbox.source || new URL(lightbox.sourceUrl).hostname.replace('www.', '')}
            </a>
          )}
        </div>
        <button
          onClick={onClose}
          className="absolute top-2 right-2 h-8 w-8 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center text-lg"
          data-testid="lightbox-close"
          aria-label="Close"
        >
          &times;
        </button>
      </div>
    </div>
  );
}
